import { BigNumber } from 'bignumber.js';

BigNumber.config({
  FORMAT: {
    prefix: '',
    decimalSeparator: '.',
    groupSeparator: ',',
    groupSize: 3,
    secondaryGroupSize: 0,
    fractionGroupSeparator: ' ',
    fractionGroupSize: 0,
    suffix: '',
  },
});

export const digits = (value?: string | number, precision?: number | undefined, ceil?: boolean): string => {
  if (typeof value === 'undefined' && !precision) {
    return '';
  }
  const hackedValue = Number(value) || 0;

  if (typeof precision === 'undefined') {
    return new BigNumber(hackedValue).toFixed();
  }
  // 整数截取
  if (precision <= 0) {
    return new BigNumber(hackedValue)
      .shiftedBy(precision)
      .integerValue(ceil ? BigNumber.ROUND_CEIL : BigNumber.ROUND_DOWN)
      .shiftedBy(-precision)
      .toFixed();
  }
  return new BigNumber(hackedValue).toFixed(precision, ceil ? BigNumber.ROUND_CEIL : BigNumber.ROUND_DOWN);
};

export const getEntryPriceRound = (positionSide: PositionSide) =>
  positionSide === 'LONG' ? BigNumber.ROUND_UP : BigNumber.ROUND_DOWN;

/** 强平价展示：开多向上取整，开空向下取整 */
export const getLiquidationPriceRound = (positionSide?: PositionSide) =>
  positionSide === 'LONG' ? BigNumber.ROUND_UP : BigNumber.ROUND_DOWN;

export const getProfitRound = (value: string | number) =>
  new BigNumber(value).gt(0) ? BigNumber.ROUND_DOWN : BigNumber.ROUND_UP;

/**
 * 计算U本位价值
 * @param price: 价格
 * @param amount: 张数
 * @param contractSize: 面值
 */
export const getValue = (price: string | number, amount: string | number, contractSize: string | number): string => {
  const result = new BigNumber(price).times(amount).times(contractSize);
  return result.isNaN() ? '0' : result.valueOf();
};

/**
 * 计算币本位价值
 * @param price: 价格
 * @param amount: 张数
 * @param contractSize: 面值
 */
export const getCoinValue = (
  price: string | number,
  amount: string | number,
  contractSize: string | number,
): string => {
  const result = new BigNumber(amount).times(contractSize).div(price);
  return result.isNaN() ? '0' : result.valueOf();
};

export const createPrecisionView = (value: number) => {
  let result = '';

  if (value) {
    result = `0.${String(1).padStart(value, '0')}`;
  }

  return result;
};

export const thousandSeparator = (value: number | string): string => {
  const temp = new BigNumber(value || 0);

  if (temp.isNaN()) {
    return '--';
  }

  let digit = 0;

  if (value) {
    digit = String(value).split('.')[1]?.length || 0;
  }

  return temp.toFormat(digit);
};

// 判断价格进度是否>=8位
export const filterBigNum = (temp: BigNumber): string => {
  // 当价格精度>=8位的时候，数量会很大，需要处理一下
  // 不到1百万显示K，比如10K
  // 不到10亿显示M，比如10M
  // 超过10亿显示B，比如10B
  if (temp.minus(1e9).toNumber() >= 0) return temp.div(1e9).toFixed(3) + 'B';
  if (temp.minus(1e6).toNumber() >= 0) return temp.div(1e6).toFixed(3) + 'M';
  if (temp.minus(1e3).toNumber() >= 0) return temp.div(1e3).toFixed(3) + 'K';

  return temp.toFixed();
};

export const kmbSeparator = (value: number | string, i18n?: string) => {
  const temp = new BigNumber(value || 0);

  if (temp.isNaN()) {
    return '--';
  }
  let digit = 0;

  if (value) {
    // 如果是科学计数法，可以快速进行处理
    if (temp.toString().indexOf('e-') >= 0) {
      const arr = String(value).split('e-');
      const len = arr[1];
      const newValue = '0.0{' + len + '}' + arr[0].toString();
      return newValue;
    }
    if (temp.toString().indexOf('e+') >= 0) {
      const arr = String(value).split('e+');
      const len = Number(arr[1] || 0);
      if (len >= 8) {
        return filterBigNum(temp);
      }
      return temp.toFormat(digit);
    }
    // 科学计数法处理结束
    let bigDigit = 0; // 整数部分位数
    const arr = String(value).split('.');
    digit = arr[1]?.length || 0;
    bigDigit = arr[0]?.length || 0;
    if (bigDigit >= 8) {
      return filterBigNum(temp);
    }

    if (arr[0] === '0') {
      const smallArr = arr[1];
      let count = 0;
      const clacLen = 6;
      if (smallArr && smallArr.length > clacLen) {
        // 递归取 0 的位数
        while (count < smallArr.length - 1) {
          if (smallArr[count] !== '0') {
            break;
          }
          count++;
        }
        if (count >= clacLen) {
          smallArr.substring(count, smallArr.length - 1);
          return '0.0{' + count + '}' + smallArr;
        }
        return temp.toFormat(digit);
      }
      return temp.toFormat(digit);
    }
  }
  return temp.toFormat(digit);
};

export const getStandardPrice = (value: string, step = '0.00000001') => {
  let result = value;
  const safeStep = step && !new BigNumber(step).isNaN() && new BigNumber(step).gt(0) ? step : '0.00000001';

  if (value) {
    const timesBN = new BigNumber(value).div(safeStep);

    if (!timesBN.isInteger()) {
      result = new BigNumber(safeStep).times(timesBN.toFixed(0, 0)).valueOf();
    }
  }

  return result;
};
